import type { Tone } from "./orders";
import type { AgentOrder, AgentShipment } from "./types";

// What the Commissions screen works out from agent_commissions: each shipment's commission status in words, what can
// be recorded next, what's left to ship, the totals, and the arguments of the acts. Pure, so it is tested without a
// browser.

const STATUS: Record<string, { text: string; tone: Tone }> = {
  EARNED: { text: "Earned, not invoiced", tone: "soon" },
  INVOICED: { text: "Invoiced", tone: "neutral" },
  COLLECTED: { text: "Collected by the supplier", tone: "neutral" },
  PAID: { text: "Paid", tone: "good" },
  LATE: { text: "Late", tone: "late" },
  TERMS_UNKNOWN: { text: "Terms not set", tone: "soon" },
};

/** A shipment's commission status in words, never colour alone. */
export function commissionStatus(status: string): { text: string; tone: Tone } {
  return STATUS[status] ?? { text: status.toLowerCase().replace(/_/g, " "), tone: "neutral" };
}

export type CommissionEvent = "INVOICED" | "COLLECTED" | "PAID";

/** The next thing to record for a shipment: invoiced, then collected, then paid; null once paid. */
export function nextEvent(s: AgentShipment): CommissionEvent | null {
  if (s.paidOn) return null;
  if (!s.invoice) return "INVOICED";
  if (!s.collectedMonth) return "COLLECTED";
  return "PAID";
}

/** What's left to ship on each line (never below zero): the shipment form starts from it. */
export function leftToShip(o: AgentOrder): { sku: string; leftEa: number }[] {
  return o.lines.map((l) => ({ sku: l.sku, leftEa: Math.max(0, l.qtyEa - l.shippedEa) }));
}

export interface CommissionTotals { expected: number; earned: number; paid: number; late: number; toInvoice: number }

export function totals(rows: AgentOrder[]): CommissionTotals {
  const shipments = rows.flatMap((o) => o.shipments);
  return {
    expected: rows.reduce((s, o) => s + (o.commissionExpected ?? 0), 0),
    earned: rows.reduce((s, o) => s + (o.commissionEarned ?? 0), 0),
    paid: rows.reduce((s, o) => s + (o.commissionPaid ?? 0), 0),
    late: shipments.filter((s) => s.status === "LATE").length,
    toInvoice: shipments.filter((s) => !s.invoice && !s.paidOn).length,
  };
}

export interface CommissionFields { invoice?: string; date?: string; month?: string; amount?: string; note?: string }

/** The arguments of record_commission for one shipment, or what's missing (in the app's words). */
export function commissionArgs(o: AgentOrder, s: AgentShipment, event: CommissionEvent, f: CommissionFields):
  { args: Record<string, unknown> } | { missing: string } {
  const base: Record<string, unknown> = { customer_po_id: o.customerPoId, release_no: s.releaseNo, event };
  if (f.note?.trim()) base.note = f.note.trim();
  if (event === "INVOICED") {
    if (!f.invoice?.trim()) return { missing: "Give Tare's invoice number from QuickBooks." };
    return { args: { ...base, invoice_number: f.invoice.trim(), ...(f.date ? { date: f.date } : {}) } };
  }
  if (event === "COLLECTED") {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(f.month ?? "")) return { missing: "Give the month the supplier collected." };
    return { args: { ...base, month: f.month } };
  }
  const amount = Number.parseFloat((f.amount ?? "").replace(/[$,]/g, ""));
  if (!Number.isFinite(amount) || amount < 0) return { missing: "Give the amount the supplier paid." };
  return { args: { ...base, amount, ...(f.date ? { date: f.date } : {}) } };
}

/** Whole EA from what was typed ("7,000"), or null when it isn't a whole number; blank is 0. */
export function wholeEa(text: string): number | null {
  const t = text.replace(/,/g, "").trim();
  if (!t) return 0;
  return /^\d+$/.test(t) ? Number.parseInt(t, 10) : null;
}

/** The lines of record_agent_shipment from the quantities typed in: blanks and zeros left out. */
export function shipmentLines(entries: { sku: string; qty: string }[]): { sku: string; qty_ea: number }[] | { problem: string } {
  const lines: { sku: string; qty_ea: number }[] = [];
  for (const e of entries) {
    const n = wholeEa(e.qty);
    if (n === null) return { problem: `${e.sku}: the quantity is a whole number of EA.` };
    if (n > 0) lines.push({ sku: e.sku, qty_ea: n });
  }
  return lines.length ? lines : { problem: "Give the quantity shipped for at least one SKU." };
}

export interface OrderLineEntry { item: string; qty: string; price: string; cost: string }

/**
 * The arguments of record_agent_order from the form, or what's missing. An item that starts TG- is a Tare SKU;
 * anything else is the customer's part number as written. Price and cost are optional (the agreed prices apply).
 */
export function agentOrderArgs(f: { customer: string; supplier: string; poNumber: string; poDate: string; wanted: string;
  market: string; note: string; lines: OrderLineEntry[] }): { args: Record<string, unknown> } | { missing: string } {
  if (!f.customer.trim()) return { missing: "Say which customer." };
  if (!f.supplier.trim()) return { missing: "Say which supplier the PO went to." };
  if (!f.poNumber.trim()) return { missing: "Give the customer's PO number, exactly as written." };
  const lines: Record<string, unknown>[] = [];
  for (const l of f.lines) {
    if (!l.item.trim() && !l.qty.trim()) continue;
    const item = l.item.trim();
    if (!item) return { missing: "Each line needs its SKU or the customer's part number." };
    const n = wholeEa(l.qty);
    if (!n) return { missing: `${item}: give the quantity, a whole number of EA.` };
    const line: Record<string, unknown> = /^TG-/i.test(item) ? { sku: item.toUpperCase() } : { customer_item_no: item };
    line.qty_ea = n;
    for (const [field, text] of [["unit_price", l.price], ["unit_cost", l.cost]] as const) {
      if (!text.trim()) continue;
      const v = Number.parseFloat(text.replace(/[$,]/g, ""));
      if (!Number.isFinite(v) || v < 0) return { missing: `${item}: the ${field === "unit_price" ? "price" : "cost"} is a number per EA.` };
      line[field] = v;
    }
    lines.push(line);
  }
  if (!lines.length) return { missing: "Add at least one line." };
  return {
    args: {
      customer: f.customer.trim(), supplier: f.supplier.trim(), po_number: f.poNumber.trim(), lines,
      ...(f.poDate ? { po_date: f.poDate } : {}), ...(f.wanted ? { wanted: f.wanted } : {}),
      ...(f.market.trim() ? { market: f.market.trim() } : {}), ...(f.note.trim() ? { note: f.note.trim() } : {}),
    },
  };
}
